import { useEffect, useMemo, useRef, useState } from "react";
import { Folder, Globe, Loader2 } from "lucide-react";
import type {
  SearchMode,
  SearchResultItem,
} from "@/types/sharepoint-search";
import { SEARCH_MODE_SITES_ONLY } from "@/types/sharepoint-search";

// Group folder rows by site when this many rows share a site_id. Fewer
// than this and the flat list is easier to scan than a labelled group.
const GROUP_FOLDER_ROWS_THRESHOLD = 3;

interface FolderSearchResultsProps {
  results: SearchResultItem[];
  searchMode: SearchMode;
  isLoading: boolean;
  onSelectFolder: (item: SearchResultItem) => void;
  onDrillIntoSite: (item: SearchResultItem) => void;
}

interface FlattenedRow {
  kind: "header" | "result";
  label?: string; // for headers
  item?: SearchResultItem; // for results
  key: string;
}

function buildRows(results: SearchResultItem[]): FlattenedRow[] {
  // Group folder rows of the same site_id contiguously. Site rows are NOT
  // bucketed -- they render in place wherever they rank. Within each bucket,
  // folder rows preserve their relative score order; buckets themselves
  // render in first-occurrence order of the site_id in the ranked input.
  const rows: FlattenedRow[] = [];
  if (results.length === 0) return rows;

  // First pass: count folders per site_id; capture site names from kind=site rows.
  const folderCountsBySite = new Map<string, number>();
  const siteNamesBySiteId = new Map<string, string>();
  for (const r of results) {
    if (r.kind === "folder" && r.site_id) {
      folderCountsBySite.set(
        r.site_id,
        (folderCountsBySite.get(r.site_id) || 0) + 1,
      );
    }
    if (r.kind === "site" && r.id && !siteNamesBySiteId.has(r.id)) {
      siteNamesBySiteId.set(r.id, r.name);
    }
  }
  const groupedSiteIds = new Set<string>();
  for (const [siteId, count] of folderCountsBySite) {
    if (count >= GROUP_FOLDER_ROWS_THRESHOLD) {
      groupedSiteIds.add(siteId);
    }
  }

  // Second pass: bucket folder rows by site_id (preserving inner order).
  const folderBuckets = new Map<string, SearchResultItem[]>();
  results.forEach((item) => {
    if (item.kind === "folder" && item.site_id) {
      if (!folderBuckets.has(item.site_id)) {
        folderBuckets.set(item.site_id, []);
      }
      folderBuckets.get(item.site_id)!.push(item);
    }
  });

  // Third pass: walk the ranked list. For sites and site-less folders, emit
  // in place. For folders whose site_id has not been flushed yet, emit the
  // optional header then the whole bucket contiguously at the site's
  // first-occurrence position.
  const emittedSiteIds = new Set<string>();
  results.forEach((item, idx) => {
    if (item.kind === "site") {
      rows.push({
        kind: "result",
        item,
        key: `${item.source}-site-${item.id ?? idx}-${idx}`,
      });
      return;
    }
    if (item.kind === "folder" && item.site_id) {
      if (emittedSiteIds.has(item.site_id)) return;
      emittedSiteIds.add(item.site_id);
      if (groupedSiteIds.has(item.site_id)) {
        const siteName =
          siteNamesBySiteId.get(item.site_id) ||
          item.breadcrumb.split(" > ")[0] ||
          "Site";
        rows.push({
          kind: "header",
          label: siteName,
          key: `header-${item.site_id}`,
        });
      }
      const bucket = folderBuckets.get(item.site_id) || [item];
      bucket.forEach((bucketItem, bIdx) => {
        rows.push({
          kind: "result",
          item: bucketItem,
          key: `${bucketItem.source}-folder-${bucketItem.id ?? bIdx}-${bIdx}`,
        });
      });
      return;
    }
    // Folder with no site_id (e.g. OneDrive) -- emit in place, no header.
    rows.push({
      kind: "result",
      item,
      key: `${item.source}-${item.kind}-${item.id ?? idx}-${idx}`,
    });
  });

  return rows;
}

export default function FolderSearchResults({
  results,
  searchMode,
  isLoading,
  onSelectFolder,
  onDrillIntoSite,
}: FolderSearchResultsProps) {
  const rows = useMemo(() => buildRows(results), [results]);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // Map flattened rows to an array of result-only indices for keyboard nav.
  const resultIndices = useMemo(
    () => rows.reduce<number[]>((acc, row, i) => {
      if (row.kind === "result") acc.push(i);
      return acc;
    }, []),
    [rows],
  );

  useEffect(() => {
    // Reset focus when the underlying result set changes.
    setFocusedIndex(-1);
  }, [results]);

  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < rowRefs.current.length) {
      rowRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (resultIndices.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const currentOrdinal = resultIndices.indexOf(focusedIndex);
      const nextOrdinal =
        currentOrdinal < 0
          ? 0
          : Math.min(currentOrdinal + 1, resultIndices.length - 1);
      setFocusedIndex(resultIndices[nextOrdinal]);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const currentOrdinal = resultIndices.indexOf(focusedIndex);
      const nextOrdinal = currentOrdinal <= 0 ? 0 : currentOrdinal - 1;
      setFocusedIndex(resultIndices[nextOrdinal]);
    }
  };

  if (isLoading && results.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2
          className="w-5 h-5 animate-spin text-primary/60"
          data-testid="folder-search-loading"
        />
      </div>
    );
  }

  if (!isLoading && results.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-muted-foreground"
        data-testid="folder-search-empty"
      >
        <Folder className="w-10 h-10 mb-3 opacity-25" />
        <p className="text-sm">No matches. Try browsing below.</p>
      </div>
    );
  }

  return (
    <div
      onKeyDown={handleContainerKeyDown}
      data-testid="folder-search-results"
    >
      {searchMode === SEARCH_MODE_SITES_ONLY && (
        <div
          className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-900"
          data-testid="sites-only-banner"
        >
          Search returned sites only. Click &quot;Open&quot; to browse for folders inside.
        </div>
      )}
      <div className="divide-y divide-border/50">
        {rows.map((row, idx) => {
          if (row.kind === "header") {
            return (
              <div
                key={row.key}
                className="px-4 py-1.5 bg-muted/30 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {row.label}
              </div>
            );
          }
          const item = row.item!;
          const isFolder = item.kind === "folder";
          const Icon = isFolder ? Folder : Globe;
          const ctaLabel = isFolder ? "Connect" : "Open";
          const ctaTestId = isFolder ? "connect-folder" : "open-site";
          const handlePrimary = () =>
            isFolder ? onSelectFolder(item) : onDrillIntoSite(item);

          return (
            <button
              key={row.key}
              ref={(el) => {
                rowRefs.current[idx] = el;
              }}
              type="button"
              data-testid="search-result-row"
              onClick={handlePrimary}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handlePrimary();
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/60 focus:bg-muted/60 focus:outline-none transition-colors group"
            >
              <span className="shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
                <Icon className="w-[18px] h-[18px]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="block text-[13px] font-medium text-foreground truncate">
                    {item.name}
                  </span>
                  {!isFolder && (
                    <span className="inline-block shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded">
                      Site
                    </span>
                  )}
                </span>
                {item.breadcrumb && (
                  <span className="block text-xs text-muted-foreground truncate">
                    {item.breadcrumb}
                  </span>
                )}
              </span>
              <span
                data-testid={ctaTestId}
                className="shrink-0 px-2.5 py-1 text-[12px] font-medium bg-primary text-primary-foreground rounded-md"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrimary();
                }}
                role="button"
                tabIndex={-1}
              >
                {ctaLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
